import { ChevronRight, Swords, Zap } from "lucide-react";

interface Participant {
  displayName: string;
  personId: string;
  team: string;
  handicap: string;
  isWinner?: boolean;
}

interface RoundHistoryItem {
  id: string;
  date: string;
  courseName: string;
  gameMode: string;
  engineType?: string;
  matchFormat: string;
  status: string;
  netResult: number;
  stakeFront: number;
  stakeBack: number;
  stakeOverall: number;
  payoutType?: string;
  isRivalry: boolean;
  rivalryContext?: string | null;
  opponents: { displayName: string; personId: string }[];
  teammates: { displayName: string; personId: string }[];
  participants: Participant[];
}

function formatMoney(amount: number): string {
  const abs = Math.abs(amount);
  return `$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatMatchup(
  teammates: { displayName: string }[],
  opponents: { displayName: string }[]
): string {
  const myTeam =
    teammates.length > 0
      ? `You & ${teammates.map((t) => t.displayName).join(" & ")}`
      : "You";
  const theirTeam = opponents.map((o) => o.displayName).join(" & ");
  return `${myTeam} vs ${theirTeam}`;
}

function getGameModePill(engineType: string): string {
  if (engineType.includes("TwoDown")) return "Nassau · Two Down Auto";
  if (engineType.includes("OnDemand")) return "Nassau · On Demand";
  return engineType;
}

interface Props {
  round: RoundHistoryItem;
  onClick: () => void;
}

export function HistoryRoundRow({ round: r, onClick }: Props) {
  const isWin = r.netResult > 0.005;
  const isLoss = r.netResult < -0.005;
  const isTie = !isWin && !isLoss;

  const engineType = r.engineType ?? r.gameMode;
  const showPayoutTag =
    r.payoutType && r.payoutType !== "Full" && r.payoutType !== "full";

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-xl border border-gray-100 p-4 active:bg-gray-50 transition-colors text-left"
      data-testid={`round-row-${r.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Win / Loss / Tie badge */}
          <span
            className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-black leading-none ${
              isWin
                ? "bg-green-100 text-green-700"
                : isLoss
                ? "bg-red-100 text-red-600"
                : "bg-gray-100 text-gray-500"
            }`}
            data-testid={`badge-result-${r.id}`}
          >
            {isWin ? "W" : isLoss ? "L" : "T"}
          </span>

          <p
            className="font-black text-base text-gray-900 leading-tight truncate"
            data-testid={`text-matchup-${r.id}`}
          >
            {formatMatchup(r.teammates, r.opponents)}
          </p>
        </div>
        <ChevronRight size={16} className="text-gray-300 mt-0.5 shrink-0" />
      </div>

      {/* Dollar amount */}
      <p
        className={`font-bold text-lg mt-1 ${
          isWin ? "text-green-600" : isLoss ? "text-red-500" : "text-gray-400"
        }`}
        data-testid={`text-net-${r.id}`}
      >
        {isWin ? "+" : ""}
        {formatMoney(r.netResult)}
      </p>

      {/* Badges row */}
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        <p className="text-xs text-gray-400">{formatDate(r.date)}</p>

        {/* Format badge */}
        <span
          className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full"
          data-testid={`badge-format-${r.id}`}
        >
          {r.matchFormat}
        </span>

        {r.isRivalry && (
          <span
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full"
            data-testid={`badge-rival-${r.id}`}
          >
            <Swords size={10} />
            Rival
          </span>
        )}

        {showPayoutTag && (
          <span
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-full"
            data-testid={`badge-payout-${r.id}`}
          >
            <Zap size={9} />
            {r.payoutType}
          </span>
        )}
      </div>

      {/* Game mode pill */}
      <p
        className="text-[11px] text-gray-400 mt-1 font-medium"
        data-testid={`text-gamemode-${r.id}`}
      >
        {getGameModePill(engineType)} · {r.courseName}
      </p>

      {/* Rivalry context */}
      {r.rivalryContext && (
        <p
          className="text-xs text-amber-600 font-semibold mt-0.5"
          data-testid={`text-rivalry-${r.id}`}
        >
          {r.rivalryContext}
        </p>
      )}
    </button>
  );
}
